/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuditLogs } from "../store/slices/auditLogsSlice";
import { fetchCompanyByType } from "../store/slices/companySlice";
import Layout from "../common/components/Layout";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Enum for Actions
const AuditAction = Object.freeze({
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  ALL: 'All'
});

const AuditLogs = () => {
  const dispatch = useDispatch();

  // Redux State
  const { list: logs = [], totalItems = 0, status } = useSelector((state) => state.audit || {});
  const { list: companyList = [] } = useSelector((state) => state.companyTypeList || {});

  /* ===================== LOCAL STATE ===================== */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedLog, setSelectedLog] = useState(null); 
  
  const [filters, setFilters] = useState({ 
    companyId: "", 
    action: AuditAction.ALL, 
    search: '' 
  });

  /* ===================== API CALLS ===================== */
  useEffect(() => {
    dispatch(fetchCompanyByType());
  }, [dispatch]);

  useEffect(() => {
    const skip = (currentPage - 1) * itemsPerPage;
    dispatch(fetchAuditLogs({ 
      companyId: filters.companyId,
      action: filters.action,
      limit: itemsPerPage, 
      skip 
    }));
  }, [dispatch, filters.companyId, filters.action, currentPage, itemsPerPage]);

  /* ===================== CLIENT-SIDE FILTERING ===================== */
  // Using useMemo to filter by employee name from the fetched array
  const filteredLogs = useMemo(() => {
    if (!filters.search.trim()) return logs;

    const searchTerm = filters.search.toLowerCase();
    return logs.filter(log => {
      const fullName = log.employee 
        ? `${log.employee.firstName} ${log.employee.lastName}`.toLowerCase() 
        : "";
      const employeeId = log.employee?.employeeId?.toLowerCase() || "";
      const reason = log.reason?.toLowerCase() || "";

      return fullName.includes(searchTerm) || 
             employeeId.includes(searchTerm) || 
             reason.includes(searchTerm);
    });
  }, [logs, filters.search]);

  /* ===================== USER-FRIENDLY MAPPING ===================== */
  const getCompanyName = (id) => {
    const company = companyList.find(c => c.id === id);
    return company ? company.companyName : "N/A";
  };

  const friendlyKey = (key) => {
    const labels = {
      branch: "Branch Location",
      department: "Department",
      jobTitle: "Job Position",
      file_url: "Attached Document",
      birthDate: "Date of Birth",
      contactNumber: "Primary Contact",
      mothersMaidenName: "Mother's Maiden Name",
      employeeStatus: "Employment Status",
      assignmentType: "Assignment Type",
      civilStatus: "Civil Status",
      id_number: "ID Number",
      docName: "Document Name",
      remarks: "System Remarks",
      gender: "Gender Identity"
    };
    return labels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const styles = {
      [AuditAction.UPDATE]: "bg-primary bg-opacity-10 text-primary border-primary",
      [AuditAction.CREATE]: "bg-success bg-opacity-10 text-success border-success",
      [AuditAction.DELETE]: "bg-danger bg-opacity-10 text-danger border-danger",
    };
    return (
      <span className={`badge rounded-pill border border-opacity-25 ${styles[action] || "bg-secondary text-white"}`} 
            style={{ fontSize: "0.65rem", padding: '6px 12px' }}>
        {action}
      </span>
    );
  };

  /* ===================== ORIGINAL PAGINATION LOGIC ===================== */
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getPaginationGroup = () => {
    const totalDisplayed = 5; 
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages;
  };

  const filterStyle = { 
    border: "1px solid rgba(0,0,0,0.1)", borderRadius: "8px", padding: "10px 14px", 
    fontSize: "0.8rem", backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(5px)", outline: "none" 
  };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container-fluid py-4 flex-grow-1 px-lg-5 w-100">
        <style>{`
          .custom-table-header th { background-color: #1e293b !important; color: #ffffff !important; font-size: 0.7rem !important; font-weight: 700 !important; letter-spacing: 0.05em !important; text-transform: uppercase !important; }
          .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; border: none; }
          .page-link-custom.active { background: #0f172a !important; color: #fff !important; }
          .page-link-custom.disabled { opacity: 0.3; cursor: not-allowed; }
          .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
          .loading-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.5); display: flex; align-items: center; justify-content: center; z-index: 10; }
          .diff-old { background-color: #fee2e2; color: #991b1b; }
          .diff-new { background-color: #dcfce7; color: #166534; }
        `}</style>
        
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0" style={{ letterSpacing: "-1.5px" }}>Audit Logs</h1>
            <p className="text-muted small mb-0">System Activity & History</p>
          </div>
        </div>

        <div className="row g-2 mb-4">
          <div className="col-md-3">
            <select className="form-select" style={filterStyle} value={filters.companyId} onChange={(e) => { setFilters({...filters, companyId: e.target.value}); setCurrentPage(1); }}>
              <option value="">All Companies</option>
              {companyList.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select" style={filterStyle} value={filters.action} onChange={(e) => { setFilters({...filters, action: e.target.value}); setCurrentPage(1); }}>
              <option value={AuditAction.ALL}>All Actions</option>
              <option value={AuditAction.CREATE}>Create</option>
              <option value={AuditAction.UPDATE}>Update</option>
              <option value={AuditAction.DELETE}>Delete</option>
            </select>
          </div>
          <div className="col-md-4">
            <input 
              type="text" 
              className="form-control" 
              placeholder="Search employee name, ID or reason..." 
              style={filterStyle} 
              value={filters.search} 
              onChange={(e) => setFilters({...filters, search: e.target.value})} 
            />
          </div>
        </div>

        <div style={{ position: 'relative', background: "rgba(255, 255, 255, 0.6)", backdropFilter: "blur(12px)", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.3)", boxShadow: "0 10px 30px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          {status === "loading" && <div className="loading-overlay"><div className="spinner-border spinner-border-sm text-primary"></div></div>}
          
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3 border-0">Timestamp</th>
                  <th className="py-3 border-0">Company</th>
                  <th className="py-3 border-0">Employee</th>
                  <th className="py-3 border-0">Action</th>
                  <th className="py-3 border-0">Performed By</th>
                  <th className="pe-4 py-3 border-0 text-end">Details</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: "0" }}>
                {filteredLogs.length === 0 && status !== "loading" ? (
                  <tr><td colSpan="6" className="text-center py-5 text-muted small">No records found.</td></tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="align-middle">
                      <td className="ps-4 py-3 fw-bold" style={{ fontSize: "0.8rem", color: '#1e293b' }}>{formatDate(log.createdAt)}</td>
                      <td style={{ fontSize: "0.75rem", color: '#475569' }}>{getCompanyName(log.companyId)}</td>
                      <td style={{ fontSize: "0.8rem", color: '#334155' }}>
                        <div className="fw-bold">{log.employee ? `${log.employee.firstName} ${log.employee.lastName}` : "System / N/A"}</div>
                        <div className="text-muted small" style={{ fontSize: '0.7rem' }}>{log.employee?.employeeId || log.entityId}</div>
                      </td>
                      <td>{getActionBadge(log.action)}</td>
                      <td style={{ fontSize: "0.8rem", color: '#334155' }}>{log.changedBy?.name || "System"}</td>
                      <td className="pe-4 text-end">
                        <button className="btn btn-sm btn-dark" style={{ fontSize: '0.65rem', borderRadius: '6px', fontWeight: '600' }} onClick={() => setSelectedLog(log)}>View Data</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show</span>
              <select 
                className="form-select form-select-sm" 
                style={{ width: 'auto', borderRadius: '6px' }} 
                value={itemsPerPage} 
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {[10, 25, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <span className="text-muted small">of <strong>{totalItems}</strong> entries</span>
            </div>
            <nav className="d-flex align-items-center">
              <button 
                className={`page-link-custom ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              {getPaginationGroup().map(p => (
                <button 
                  key={p} 
                  className={`page-link-custom ${currentPage === p ? 'active' : ''}`} 
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}
              <button 
                className={`page-link-custom ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {selectedLog && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Detailed Changes</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedLog(null)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="table-responsive border rounded">
                  <table className="table table-sm mb-0 align-middle">
                    <thead className="bg-light">
                      <tr style={{ fontSize: '0.7rem' }}>
                        <th className="ps-3 py-2 text-muted">DATA FIELD</th>
                        <th className="py-2 text-muted">FROM (PREVIOUS)</th>
                        <th className="py-2 text-muted">TO (UPDATED)</th>
                      </tr>
                    </thead>
                    <tbody style={{ fontSize: '0.8rem' }}>
                      {Object.keys(selectedLog.newValues || {}).map((key) => {
                        const oldValue = selectedLog.oldValues?.[key];
                        const newValue = selectedLog.newValues?.[key];
                        if (oldValue === newValue) return null;

                        return (
                          <tr key={key}>
                            <td className="ps-3 py-2 fw-bold text-dark">{friendlyKey(key)}</td>
                            <td className="py-2 diff-old px-2">
                              {oldValue === null ? <em className="opacity-50">Empty</em> : String(oldValue)}
                            </td>
                            <td className="py-2 diff-new px-2">
                              {key === 'file_url' && newValue ? (
                                <a href={newValue} target="_blank" rel="noreferrer" className="fw-bold text-success text-decoration-underline">View S3 File</a>
                              ) : (
                                String(newValue)
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 p-3 bg-light rounded-3">
                  <span className="small text-muted d-block mb-1">Reason for Activity:</span>
                  <span className="fw-bold text-primary">{selectedLog.reason}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AuditLogs;